import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import FirebaseImage from '../../Components/Media/FirebaseImage';
import Loader from '../../Components/UI/Loader/Loader';
import { getDemandsAPI } from '../../Store/Actions/demandActions';
import { formatDate } from '../../Utils/date';
import { getArtisanName, getProjectDescription, getProjectImages } from '../../Utils/legacyData';
import { subscribePublishedReviews } from '../../Services/trustReviews';
import ClientPreviousProjectModal from './ClientPreviousProjectModal';
import ProjectInvoiceModal from '../../Components/Invoicing/ProjectInvoiceModal';
import { useI18n } from '../../i18n/I18nProvider';

const MainProjects = ({ firebase, loading, userDemands = [], getDemands }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [reviewsByDemand, setReviewsByDemand] = useState({});
  const [invoiceDemandId, setInvoiceDemandId] = useState('');
  const { language } = useI18n();
  const en = language === 'en';
  const clientId = firebase?.auth?.uid;

  useEffect(() => {
    const unsubscribe = getDemands();
    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [getDemands]);

  useEffect(() => {
    if (!clientId) return undefined;
    return subscribePublishedReviews(
      { reviewerId: clientId },
      (reviews) => setReviewsByDemand(Object.fromEntries(reviews.map((review) => [review.demand_id, review]))),
      () => setReviewsByDemand({}),
    );
  }, [clientId]);

  const completedProjects = useMemo(
    () => userDemands.filter((demand) => (
      demand?.user_id === clientId
      && demand?.demand_status === 'reviewed'
      && demand?.contract
    )),
    [userDemands, clientId],
  );

  return (
    <Section>
      {loading && completedProjects.length === 0 ? (
        <LoadingCard><Loader /></LoadingCard>
      ) : completedProjects.length === 0 ? (
        <Empty>
          <Icon>✓</Icon>
          <h2>Aucun projet clôturé</h2>
          <p>Vos projets terminés et évalués apparaîtront ici avec leur contrat et leurs photos.</p>
        </Empty>
      ) : (
        <Gallery>
          {completedProjects.map((project) => {
            const images = getProjectImages(project);
            const image = images[0] || '';
            const rating = Number(reviewsByDemand[project.demand_id]?.rating || 0);
            const artisanName = getArtisanName(project);
            const description = getProjectDescription(project);

            return (
              <ProjectCard key={project.demand_id}>
                <CardOpen type="button" onClick={() => setSelectedProject(project)}>
                  <ImageWrap>
                    <ProjectImage src={image} fallbackSrc="/images/project-placeholder.svg" alt={en ? 'Completed project' : 'Projet terminé'} />
                    {rating > 0 && <Rating>★ {rating}/5</Rating>}
                  </ImageWrap>
                  <CardBody>
                    <DateText>{en ? 'Completed on' : 'Terminé le'} {formatDate(project.contract?.end_date, formatDate(project.date))}</DateText>
                    <h3>{description}</h3>
                    <p>{en ? 'Work by' : 'Intervention de'} {artisanName}</p>
                    <OpenText>{en ? 'View photos and contract →' : 'Voir les photos et le contrat →'}</OpenText>
                  </CardBody>
                </CardOpen>
                {project.invoicing?.client_visible === true && (
                  <InvoiceButton type="button" onClick={() => setInvoiceDemandId(project.demand_id)}>
                    {en ? 'View final invoice' : 'Voir la facture finale'}
                  </InvoiceButton>
                )}
              </ProjectCard>
            );
          })}
        </Gallery>
      )}

      <ClientPreviousProjectModal
        showModal={selectedProject ? 'open' : 'close'}
        handleClick={() => setSelectedProject(null)}
        demand={selectedProject}
      />
      <ProjectInvoiceModal
        demandId={invoiceDemandId}
        open={Boolean(invoiceDemandId)}
        onClose={() => setInvoiceDemandId('')}
      />
    </Section>
  );
};

const Section = styled.section`min-width: 0;`;
const Gallery = styled.div`display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; @media (max-width: 980px) { grid-template-columns: 1fr; }`;
const ProjectCard = styled.article`
  min-width:0;overflow:hidden;border:1px solid var(--color-border);border-radius:1.2rem;background:#fff;color:var(--color-text);box-shadow:var(--shadow-sm);transition:transform 160ms ease,box-shadow 160ms ease;
  &:hover{transform:translateY(-3px);box-shadow:0 1.2rem 2.8rem rgba(0,0,0,.13)}
`;
const CardOpen = styled.button`width:100%;padding:0;border:0;background:transparent;color:inherit;font:inherit;text-align:left;cursor:pointer;`;
const InvoiceButton = styled.button`margin:0 1rem 1rem;border:0;border-radius:999px;padding:.75rem 1rem;background:#111;color:#fff;font:inherit;font-weight:900;cursor:pointer;`;
const ImageWrap = styled.div`position: relative;`;
const ProjectImage = styled(FirebaseImage)`width: 100%; height: 13rem; object-fit: cover; background: #f1ece5;`;
const Rating = styled.span`position: absolute; top: 0.75rem; right: 0.75rem; padding: 0.38rem 0.58rem; border-radius: 999px; background: rgba(255,255,255,0.94); color: #765519; font-size: 0.78rem; font-weight: 900; box-shadow: 0 0.4rem 1rem rgba(0,0,0,0.12);`;
const CardBody = styled.div`
  padding: 1rem;
  h3 { margin: 0.35rem 0; overflow: hidden; font-size: 1.05rem; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  p { margin: 0; color: var(--color-text-muted); font-size: 0.86rem; }
`;
const DateText = styled.span`color: #75828b; font-size: 0.76rem;`;
const OpenText = styled.span`display: block; margin-top: 0.8rem; color: #8a641a; font-size: 0.82rem; font-weight: 900;`;
const Empty = styled.div`padding: 2.2rem; border: 1px dashed #ddd3c4; border-radius: 1.2rem; background: #fff; color: var(--color-text-muted); text-align: center; h2 { margin: 0.6rem 0 0.35rem; color: var(--color-text); } p { margin: 0; }`;
const Icon = styled.div`display: grid; place-items: center; width: 3.5rem; height: 3.5rem; margin: 0 auto; border-radius: 50%; background: #eef7f1; color: #2a7a4f; font-size: 1.5rem; font-weight: 900;`;
const LoadingCard = styled.div`display: grid; place-items: center; min-height: 15rem; border-radius: 1.2rem; background: #fff; box-shadow: var(--shadow-sm);`;

const mapStateToProps = ({ firebase, demands }) => ({
  firebase,
  loading: demands?.loading,
  userDemands: demands?.demands || [],
});
const mapDispatchToProps = (dispatch) => ({ getDemands: () => dispatch(getDemandsAPI()) });

export default connect(mapStateToProps, mapDispatchToProps)(MainProjects);
